import fallbackImage from "@assets/images/fallback-game.svg";
import {useQueries} from "@tanstack/react-query";
import {useCallback, useMemo, useState} from "react";
import {getTopGamesService} from "@/services/games/gamesList/topGames.service";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";

const TOP_GAMES_LIMIT = 3;
const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 10 * 60 * 1000;

const enrichTopGames = (games, getGameById) =>
  games.map(topGame => {
    const enriched = getGameById(topGame.game_id, topGame.game_provider);
    return {
      id: enriched?.id || topGame.game_id,
      name: enriched?.name || topGame.game_name,
      provider: enriched?.provider || topGame.game_provider,
      icon: enriched?.icon || fallbackImage,
      thumbnail_url: enriched?.thumbnail_url || enriched?.icon || fallbackImage,
    };
  });

/**
 * Lazily fetches top (most-played) games per provider on demand.
 * Call requestProviders() to load one or more providers; React Query caches each separately.
 *
 * Backend endpoint: GET /v1/games/top?provider=X&limit=3
 *
 * @returns {{
 *   topGamesByProvider: Record<string, Array<{id: string, name: string, provider: string, icon: string, thumbnail_url: string}>>,
 *   requestProviders: (providerKeys: string[]) => void,
 * }}
 */
export const useProviderTopGames = () => {
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);
  const [requestedKeys, setRequestedKeys] = useState([]);

  const requestProviders = useCallback(providerKeys => {
    if (!providerKeys?.length) return;
    setRequestedKeys(prev => {
      const next = new Set(prev);
      for (const key of providerKeys) {
        if (key) next.add(key);
      }
      return next.size === prev.length ? prev : [...next];
    });
  }, []);

  const queries = useQueries({
    queries: requestedKeys.map(provider => ({
      queryKey: ["providerTopGames", provider],
      queryFn: async () => {
        const response = await getTopGamesService({provider, limit: TOP_GAMES_LIMIT});
        return {provider, games: response?.data?.data || []};
      },
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
    })),
  });

  const topGamesByProvider = useMemo(() => {
    if (!gamesInitialized) return {};

    const map = {};
    for (const query of queries) {
      if (!query.data) continue;
      const {provider, games} = query.data;
      map[provider] = enrichTopGames(games, getGameById);
    }
    return map;
  }, [queries, gamesInitialized, getGameById]);

  return {topGamesByProvider, requestProviders};
};
